import React, { useState, useCallback } from "react";
import { Avatar, Popover, Form } from "antd";
import { MailOutlined, PhoneOutlined } from "@ant-design/icons";
import { formatPhoneNumber } from "react-phone-number-input";
import CustomButton from "../../../commonComponents/button";
import { closeDrawer, openDrawer, updateDrawer } from "../../../../library/helpers";
import { DRAWER_TYPES } from "../../../hoc/Drawer/drawer";
import AntdDrawerFooter from "../../../hoc/Drawer/AntdDrawerFooter";
import NotesPreferences from "./NotesPreferences";

const convertToInitials = fullName => {
  if (!fullName) return "";
  return fullName
    .split(" ")
    .map(name => name.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const UserHoverCard = ({
  children,
  user,
  messageContent,
  userName,
  messageId,
  messageType,
  placement = "topLeft",
  messageNotesCount = 0,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [form] = Form.useForm();

  const userEmail = user?.email?.trim() || "N/A";
  const userPhone = user?.phone?.trim() || "N/A";
  const userCompanyRole = user?.companyRole?.name || "N/A";

  const handleFormSubmit = useCallback(() => {
    form.submit();
  }, [form]);

  // preferences are handled inside NotesPreferences; we will receive state via callback
  const handlePrefsChange = nextSettings => {
    // push preferences into the drawer state so the drawer content can access them
    updateDrawer({ preferences: nextSettings });
  };

  const handleSendMessage = () => {
    // Reset form fields before opening drawer
    form.resetFields();

    // close popover immediately when opening drawer
    setIsPopoverOpen(false);

    openDrawer(
      DRAWER_TYPES.SEND_DIRECT_MESSAGE,
      {
        title: (
          <div className="flex items-center justify-between">
            <p>Message note</p>
            <NotesPreferences onChange={handlePrefsChange} />
          </div>
        ),
        footer: (
          <AntdDrawerFooter
            saveText="Send"
            onSave={handleFormSubmit}
            onCancel={() => closeDrawer(DRAWER_TYPES.SEND_DIRECT_MESSAGE)}
          />
        ),
      },
      {
        saveText: "Send",
        messageId: messageId,
        user: user,
        form: form,
        messageContent: messageContent,
        messageType: messageType,
        messageNotesCount: messageNotesCount,
        onSave: handleFormSubmit,
        successCallback: () => closeDrawer(DRAWER_TYPES.SEND_DIRECT_MESSAGE),
      }
    );
  };

  const HoverContent = () => (
    <div className="w-64 flex flex-col gap-6 p-1 bg-white">
      <div className="flex items-start gap-3">
        <Avatar
          src={user?.profilePic || ""}
          size={40}
          className={`${user?.profilePic ? "" : "bg-[#13C2C2]"} flex items-center justify-center`}
        >
          <span className="text-white font-semibold text-lg">{convertToInitials(userName)}</span>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-black/[0.88] mb-1">
            {userName || "Unknown User"}
          </div>
          <div className="text-black/[0.65] font-normal text-xs">{userCompanyRole}</div>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2.5 text-sm font-normal">
          <MailOutlined className="text-base" />
          <p className="max-w-[90%] break-words">{userEmail}</p>
        </div>
        <div className="flex items-center gap-2.5 text-sm font-normal">
          <PhoneOutlined className="text-base" />
          <p>{formatPhoneNumber(userPhone)}</p>
        </div>
      </div>
      <CustomButton
        handleClick={handleSendMessage}
        type="primary"
        btnText="Send Message"
        className="w-full"
      />
    </div>
  );

  return (
    <Popover
      content={<HoverContent />}
      title={null}
      trigger="hover"
      placement={placement}
      arrow={false}
      open={isPopoverOpen}
      onOpenChange={setIsPopoverOpen}
      mouseEnterDelay={0.3}
      mouseLeaveDelay={0.2}
    >
      {children}
    </Popover>
  );
};

export default UserHoverCard;
